import { Field } from '@today/ui-components'
import { HelpText } from '@today/ui-primitives'
import type { OidcDraft } from './use-provider-form-state'

/**
 * Props for {@link OidcFields}.
 *
 * @property draft - The OIDC inputs as typed. `clientSecret` is never prefilled.
 * @property onChange - Reports one changed input.
 * @property clientSecretSet - A secret is already stored; a blank secret input keeps it.
 * @property isDisabled - Disables every input.
 */
export interface OidcFieldsProps {
	draft: OidcDraft
	onChange: <K extends keyof OidcDraft>(key: K, value: OidcDraft[K]) => void
	clientSecretSet?: boolean
	isDisabled?: boolean
}

type TextKey = Exclude<keyof OidcDraft, 'clientSecret'>

const ENDPOINTS: { key: TextKey; label: string }[] = [
	{ key: 'authorizationEndpoint', label: 'Authorization endpoint (optional)' },
	{ key: 'tokenEndpoint', label: 'Token endpoint (optional)' },
	{ key: 'userinfoEndpoint', label: 'Userinfo endpoint (optional)' },
	{ key: 'jwksUri', label: 'JWKS URI (optional)' },
]

/** The write-only client secret: never shown, and a blank value keeps the stored one. */
function ClientSecretField({ draft, onChange, clientSecretSet = false, isDisabled }: OidcFieldsProps) {
	return (
		<Field
			label="Client secret"
			type="password"
			autoComplete="off"
			placeholder={clientSecretSet ? 'Secret set — leave empty to keep it' : ''}
			description={
				clientSecretSet
					? 'A secret is stored. It is never shown; type a new one only to replace it.'
					: 'Stored in the secret store only, and never shown again.'
			}
			value={draft.clientSecret}
			disabled={isDisabled}
			onChange={(event) => onChange('clientSecret', event.target.value)}
		/>
	)
}

/**
 * The OIDC half of {@link ProviderForm}. The provider is federated through
 * Cognito (TO-378); the client secret is write-only.
 */
export function OidcFields(props: OidcFieldsProps) {
	const { draft, onChange, isDisabled = false } = props
	const text = (key: TextKey, label: string, description?: string) => (
		<Field
			key={key}
			label={label}
			description={description}
			value={draft[key]}
			disabled={isDisabled}
			onChange={(event) => onChange(key, event.target.value)}
		/>
	)
	return (
		<div className="flex flex-col gap-3">
			<HelpText>
				Register the redirect URL in your identity provider, then run a test login. The provider is offered at sign-in
				once it is enabled and its test login has passed.
			</HelpText>
			{text('issuer', 'Issuer URL')}
			{text('clientId', 'Client ID')}
			<ClientSecretField {...props} isDisabled={isDisabled} />
			{text('scopes', 'Scopes', 'Space-separated; must include openid.')}
			{ENDPOINTS.map((endpoint) => text(endpoint.key, endpoint.label))}
			{text('email', 'Email claim')}
			{text('groups', 'Groups claim (optional)')}
		</div>
	)
}
